import React from "react";
import { motion } from "framer-motion";
import UsePageTitle from "../components/UsePageTitle";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import Navbar from "../components/Navbar/Navbar";
import LogoDark from "../resources/images/LogoDark.svg";
import axios from "axios";
import { Link } from "react-router-dom";

const ReachOut = () => {
  UsePageTitle("Reach Out | Bravix Group");
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      reasonOfConnect: "",
      agreeToComms: false,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().required("Phone number is required"),
      reasonOfConnect: Yup.string().required("Role is required"),
      agreeToComms: Yup.boolean().oneOf(
        [true],
        "Please agree to receive communications to submit the form"
      ),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      console.log("Form Values before submission:", values);
      try {
        const response = await axios.post(
          "https://bravixgroup.com/api/v1/connect-us",
          values
        );

        console.log("API Response:", response?.data);

        if (!response?.data?.error) {
          enqueueSnackbar(
            response?.data?.message || "Form submitted successfully!",
            { variant: "success" }
          );
          resetForm();
        } else {
          enqueueSnackbar(response?.data?.message || "Something went wrong!", {
            variant: "error",
          });
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Failed to submit the form.";
        enqueueSnackbar(errorMessage, { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const leftContainerVariant = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const childVariant = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };
  return (
    <>
      <Navbar />
      <section className="react_out_section">
        <div className="container h-100">
          <div className="row h-100">
            <div className="react_out_content_main">
              <div className="react_out_content">
                <motion.div
                  className="react_out_content_left"
                  variants={leftContainerVariant}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ amount: 0.3 }}
                >
                  <motion.img
                    src={LogoDark}
                    alt="Logo"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ amount: 0.3 }}
                  />
                  <motion.h5 variants={childVariant}>let’s connect!</motion.h5>
                  <motion.p variants={childVariant}>
                    Let's discuss how Bravix Group can support your goals.
                  </motion.p>
                  <motion.p
                    className="react_out_phone"
                    variants={childVariant}
                  >
                    Call us: <a href="tel:+18602953364">+1 860 295 3364</a>
                  </motion.p>
                </motion.div>

                <div className="react_out_content_right">
                  <form
                    noValidate
                    onSubmit={formik.handleSubmit}
                    autoComplete="off"
                    method="POST"
                  >
                    <div className="input_main">
                      <div className="input_group">
                        <input
                          type="text"
                          className="form-control"
                          id="firstName"
                          name="firstName"
                          placeholder="First Name"
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.firstName &&
                          formik.errors.firstName && (
                            <div className="error">
                              {formik.errors.firstName}
                            </div>
                          )}
                      </div>
                      <div className="input_group">
                        <input
                          type="text"
                          className="form-control"
                          id="lastName"
                          name="lastName"
                          placeholder="Last Name"
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.lastName && formik.errors.lastName && (
                          <div className="error">{formik.errors.lastName}</div>
                        )}
                      </div>
                    </div>

                    <div className="input_group">
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        placeholder="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="error">{formik.errors.email}</div>
                      )}
                    </div>

                    <div className="input_group">
                      <PhoneInput
                        id="phone"
                        name="phone"
                        defaultCountry="US"
                        value={formik.values.phone}
                        onChange={(value) =>
                          formik.setFieldValue("phone", value)
                        }
                        placeholder="Phone"
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <div className="error">{formik.errors.phone}</div>
                      )}
                    </div>

                    <div className="input_group">
                      <textarea
                        type="text"
                        className="form-control"
                        id="reasonOfConnect"
                        name="reasonOfConnect"
                        placeholder="Reason for connecting"
                        value={formik.values.reasonOfConnect}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.reasonOfConnect &&
                        formik.errors.reasonOfConnect && (
                          <div className="error">
                            {formik.errors.reasonOfConnect}
                          </div>
                        )}
                    </div>

                    <div className="consent_block">
                      <p className="consent_intro">
                        Bravix Group is committed to protecting and respecting
                        your privacy, and we'll only use your personal
                        information to administer your account and to provide the
                        services you requested from us. From time to time, we
                        would like to contact you about our services, as well as
                        other content that may be of interest to you. If you
                        consent to us contacting you for this purpose, please
                        tick below to say how you would like us to contact you:
                      </p>

                      <label className="consent_check">
                        <input
                          type="checkbox"
                          id="agreeToComms"
                          name="agreeToComms"
                          checked={formik.values.agreeToComms}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <span>
                          I agree to receive communications, including SMS text
                          messages, from Bravix Group.{" "}
                          <span className="req">*</span>
                        </span>
                      </label>
                      {formik.touched.agreeToComms &&
                        formik.errors.agreeToComms && (
                          <div className="error">
                            {formik.errors.agreeToComms}
                          </div>
                        )}

                      <p className="consent_note">
                        You can unsubscribe from these communications at any
                        time. Reply STOP to opt out of SMS messages, or reply
                        HELP for assistance. Message and data rates may apply.
                        For more information on how we protect your privacy,
                        please review our{" "}
                        <Link to="/privacy-policy">Privacy Policy</Link>.
                      </p>

                      <p className="consent_note">
                        By clicking submit below, you consent to allow Bravix
                        Group to store and process the personal information
                        submitted above to provide you the content requested.
                      </p>
                    </div>

                    <button type="submit" className="submit_btn">
                      Submit
                    </button>
                  </form>
                  {/* <Link to="/">Go Back</Link> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReachOut;
